import classNames from 'classnames'
import React, { CSSProperties, HTMLAttributes, PropsWithChildren, forwardRef } from 'react'

import {
  NeutralInverseWhiteColorType,
  OutlineColorType,
  SurfaceColorType,
} from '@ui/design-system/colors/colors'
import type { ElevationType } from '@ui/design-system/elevation'
import { RadiusType } from '@ui/design-system/radius'
import { SpacingType } from '@ui/design-system/spacing'

import styles from './Box.module.scss'

type FlexType = boolean | CSSProperties['flex']
type Breakpoint = 'desktop' | 'mobile'
type ResponsiveValue<T> = { mobile: T; desktop?: T } | { mobile?: T; desktop: T }
type ResponsiveProp<T> = T | ResponsiveValue<T>
type BorderType =
  | OutlineColorType
  | { color: OutlineColorType; width?: string; style?: CSSProperties['borderStyle'] }
type BorderProp = 'border' | 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft'

interface BoxStyleProps {
  textAlign?: CSSProperties['textAlign']
  cursor?: CSSProperties['cursor']

  radius?: RadiusType
  bg?: SurfaceColorType | NeutralInverseWhiteColorType
  shadow?: ElevationType

  flex?: FlexType
  grid?: boolean
  gridTemplateColumns?: CSSProperties['gridTemplateColumns']
  gridColumn?: CSSProperties['gridColumn']
  align?: CSSProperties['alignItems']
  justify?: CSSProperties['justifyContent']
  direction?: CSSProperties['flexDirection']
  wrap?: boolean
  gap?: SpacingType
  alignSelf?: CSSProperties['alignSelf']
  justifySelf?: CSSProperties['justifySelf']
  flexItem?: CSSProperties['flex']

  p?: SpacingType
  px?: SpacingType
  py?: SpacingType
  pt?: SpacingType
  pr?: SpacingType
  pb?: SpacingType
  pl?: SpacingType

  m?: SpacingType
  mx?: SpacingType
  my?: SpacingType
  mt?: SpacingType
  mr?: SpacingType
  mb?: SpacingType
  ml?: SpacingType

  fullWidth?: boolean
  fullHeight?: boolean
  maxHeight?: CSSProperties['maxHeight']
  minHeight?: CSSProperties['minHeight']
  maxWidth?: CSSProperties['maxWidth']
  minWidth?: CSSProperties['minWidth']

  border?: BorderType
  borderTop?: BorderType
  borderRight?: BorderType
  borderBottom?: BorderType
  borderLeft?: BorderType

  overflowX?: CSSProperties['overflowX']
  overflowY?: CSSProperties['overflowY']
  position?: CSSProperties['position']
  zIndex?: CSSProperties['zIndex']
}

type ResponsiveBoxStyleProps = {
  [Prop in keyof BoxStyleProps]: ResponsiveProp<NonNullable<BoxStyleProps[Prop]>>
}

export interface BoxProps
  extends PropsWithChildren,
    HTMLAttributes<HTMLDivElement>,
    ResponsiveBoxStyleProps {}

type StaticBoxProps = Omit<BoxProps, keyof BoxStyleProps> & BoxStyleProps

export const Box = forwardRef<HTMLDivElement | null, BoxProps>((props, ref) => {
  const {
    flex,
    grid,
    gridTemplateColumns,
    gridColumn,
    align,
    textAlign,
    justify,
    direction,
    wrap,
    gap,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    radius = 'x0',
    bg,
    shadow,
    border,
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
    className,
    children,
    onClick,
    fullHeight,
    fullWidth,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    style,
    alignSelf,
    justifySelf,
    cursor,
    overflowX,
    overflowY,
    position,
    flexItem,
    zIndex,
    ...rest
  } = resolveResponsiveProps(props)

  const cls = classNames(
    styles.box,

    gap && styles[`gap-${gap}`],

    p && styles[`p-${p}`],
    px && styles[`px-${px}`],
    py && styles[`py-${py}`],
    pt && styles[`pt-${pt}`],
    pr && styles[`pr-${pr}`],
    pb && styles[`pb-${pb}`],
    pl && styles[`pl-${pl}`],

    m && styles[`m-${m}`],
    mx && styles[`mx-${mx}`],
    my && styles[`my-${my}`],
    mt && styles[`mt-${mt}`],
    mr && styles[`mr-${mr}`],
    mb && styles[`mb-${mb}`],
    ml && styles[`ml-${ml}`],
    shadow && styles[`elevation-${shadow}`],

    bg,
    className,

    {
      [styles.fullWidth]: fullWidth,
      [styles.fullHeight]: fullHeight,
      [styles.flex]: flex !== undefined && flex !== false,
      [styles.grid]: grid,
      [styles.clickable]: !!onClick,
      [styles[`radius-${radius}`]]: radius,
    }
  )

  const inlineStyle: CSSProperties = {
    ...style,
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(minHeight !== undefined ? { minHeight } : {}),
    ...(maxWidth !== undefined ? { maxWidth } : {}),
    ...(maxHeight !== undefined ? { maxHeight } : {}),
    ...(gridTemplateColumns !== undefined ? { gridTemplateColumns } : {}),
    ...(gridColumn !== undefined ? { gridColumn } : {}),
    ...(direction ? { flexDirection: direction } : {}),
    ...(typeof wrap === 'boolean' ? { flexWrap: wrap ? 'wrap' : 'nowrap' } : {}),
    ...(align ? { alignItems: align } : {}),
    ...(textAlign ? { textAlign } : {}),
    ...(justify ? { justifyContent: justify } : {}),
    ...(alignSelf ? { alignSelf } : {}),
    ...(justifySelf ? { justifySelf } : {}),
    ...getBorderStyle(border),
    ...getBorderStyle(borderTop, 'borderTop'),
    ...getBorderStyle(borderRight, 'borderRight'),
    ...getBorderStyle(borderBottom, 'borderBottom'),
    ...getBorderStyle(borderLeft, 'borderLeft'),
    ...(cursor ? { cursor } : {}),
    ...(overflowX ? { overflowX } : {}),
    ...(overflowY ? { overflowY } : {}),
    ...(position ? { position } : {}),
    ...(typeof flex !== 'boolean' && flex !== undefined ? { flex } : {}),
    ...(flexItem ? { flex: flexItem } : {}),
    ...(zIndex !== undefined ? { zIndex } : {}),
  }

  return (
    <div className={cls} onClick={onClick} style={inlineStyle} {...rest} ref={ref}>
      {children}
    </div>
  )
})

type ResponsiveStyle = CSSProperties &
  Partial<Record<`--box-${string}-${Breakpoint}`, string | number>>

const breakpoints: Breakpoint[] = ['desktop', 'mobile']
const boxStyles = styles as Record<string, string>

const resolveResponsiveProps = (props: BoxProps): StaticBoxProps => {
  if (!hasResponsiveValue(props)) return props as StaticBoxProps

  const entries = Object.entries(props)
  const staticProps = { ...props } as StaticBoxProps
  const responsiveClassNames: string[] = []
  const responsiveStyle: ResponsiveStyle = {}

  entries.forEach(([prop, value]) => {
    if (!isResponsiveValue(value)) return

    delete staticProps[prop as keyof StaticBoxProps]

    breakpoints.forEach((breakpoint) => {
      const breakpointValue = value[breakpoint]
      if (breakpointValue === undefined) return

      responsiveClassNames.push(boxStyles[`responsive-${prop}-${breakpoint}`])
      Object.assign(responsiveStyle, getResponsiveStyle(prop, breakpoint, breakpointValue))
    })
  })

  return {
    ...staticProps,
    className: classNames(responsiveClassNames, staticProps.className),
    style: { ...staticProps.style, ...responsiveStyle },
  }
}

const getResponsiveStyle = (
  prop: string,
  breakpoint: Breakpoint,
  value: unknown
): ResponsiveStyle => {
  if (prop === 'flex') {
    return {
      [`--box-flex-${breakpoint}`]: value === false ? 'block' : 'flex',
      ...(typeof value !== 'boolean'
        ? { [`--box-flex-value-${breakpoint}`]: value as string | number }
        : {}),
    }
  }

  if (prop.startsWith('border')) {
    const { color, style, width } = getBorderValue(value as BorderType)

    return {
      [`--box-${prop}-color-${breakpoint}`]: color,
      [`--box-${prop}-style-${breakpoint}`]: style,
      [`--box-${prop}-width-${breakpoint}`]: width,
    }
  }

  return { [`--box-${prop}-${breakpoint}`]: toCssValue(prop, value) }
}

const toCssValue = (prop: string, value: unknown): string | number => {
  if (isSpacingProp(prop)) return `var(--spacing-${value})`
  if (prop === 'radius') return `var(--border-radius-${value})`
  if (prop === 'bg') return `var(--${value})`
  if (prop === 'shadow') return `var(--elevation-${value})`
  if (prop === 'wrap') return value ? 'wrap' : 'nowrap'
  if (prop === 'grid') return value ? 'grid' : 'block'
  if (prop === 'fullWidth' || prop === 'fullHeight') return value ? '100%' : 'auto'
  if (prop === 'gridTemplateColumns' && typeof value === 'number') return `${value}px`
  if (/^(min|max)(Height|Width)$/.test(prop) && typeof value === 'number') return `${value}px`

  return value as string | number
}

const isSpacingProp = (prop: string) => prop === 'gap' || /^[mp][xytrbl]?$/.test(prop)

const isResponsiveValue = (value: unknown): value is ResponsiveValue<unknown> =>
  typeof value === 'object' && value !== null && ('mobile' in value || 'desktop' in value)

const hasResponsiveValue = (props: BoxProps) => {
  for (const prop in props) {
    if (isResponsiveValue(props[prop as keyof BoxProps])) return true
  }

  return false
}

const getBorderStyle = (border?: BorderType, prop: BorderProp = 'border'): CSSProperties => {
  if (!border) return {}

  const { color, style, width } = getBorderValue(border)

  return {
    [`${prop}Color`]: color,
    [`${prop}Style`]: style,
    [`${prop}Width`]: width,
  } as CSSProperties
}

const getBorderValue = (border: BorderType) => {
  const {
    color,
    style = 'solid',
    width = '1px',
  } = typeof border === 'string' ? { color: border } : border

  return {
    color: `var(--${color})`,
    style,
    width,
  }
}
